import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, LinearProgress, MenuItem, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, Delete, Edit, SwapHoriz } from '@mui/icons-material';
import api from '../../services/api';
import type { LocationKind, LocationMovement, LocationMovementType, StorageItem, StorageLocation } from '../../types/factory';
import { MaterialSelect, PageHeader, ProductSelect, addDaysIso, errorMessage, fmtQty, loadMaterials, loadProducts, num, todayIso, usePageView } from './shared';
import type { PageView } from './shared';

const KINDS: LocationKind[] = ['Insumos', 'Terminados', 'Mixto'];
const MOVES: { value: LocationMovementType; label: string; help: string }[] = [
  { value: 'IN', label: 'Ingreso', help: 'Mercadería que se guarda en una ubicación (lo recibido o lo producido).' },
  { value: 'OUT', label: 'Retiro', help: 'Sale de una ubicación (a producción, a un cliente).' },
  { value: 'TRANSFER', label: 'Traslado', help: 'Cambia de lugar: el stock total de la fábrica no cambia.' },
  { value: 'COUNT', label: 'Conteo', help: 'Cantidad física contada: deja asentada la diferencia y su motivo.' },
];
const MOVE_LABEL: Record<string, string> = { IN: 'Ingreso', OUT: 'Retiro', TRANSFER: 'Traslado', COUNT: 'Conteo' };

const VIEWS: PageView[] = [
  { key: 'ubicaciones', label: 'Ubicaciones del depósito', description: 'Racks y estantes: un código simple por lugar (ej. A1).' },
  { key: 'donde', label: 'Dónde está cada cosa', description: 'Cuánto hay de cada producto e insumo en cada ubicación. Lo que no está ubicado figura como "sin ubicar".' },
  { key: 'movimientos', label: 'Traslados y conteos', description: 'Ingresos, retiros, traslados entre ubicaciones y conteos físicos.' },
];

/** Depósito: ubicaciones (sector, rack, estante), dónde está cada cosa y sus movimientos. */
const FactoryStoragePage: React.FC = () => {
  const { tab, setTab, showTabs, flat, current } = usePageView(VIEWS);
  const [locations, setLocations] = useState<StorageLocation[]>([]);
  const [moving, setMoving] = useState<Partial<MoveForm> | null>(null);
  const [version, setVersion] = useState(0);

  const loadLocations = useCallback(async () => setLocations(await api.getStorageLocations(true)), []);
  useEffect(() => { loadLocations().catch(() => undefined); }, [loadLocations, version]);

  return (
    <Box>
      <PageHeader
        title={flat ? current.label : 'Depósito'}
        description={flat && current.description ? current.description : <>Cuánto hay y dónde está, de insumos y de productos terminados. Lo que no está en ninguna ubicación figura como "sin ubicar".</>}
        actions={<>
        <Button variant="contained" startIcon={<SwapHoriz />} onClick={() => setMoving({})} disabled={locations.filter(l => l.isActive).length === 0}>
          Registrar movimiento
        </Button>
        </>}
      />
      {showTabs && (<Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Ubicaciones" />
        <Tab label="Dónde está" />
        <Tab label="Movimientos" />
      </Tabs>)}
      {tab === 0 && <LocationsTab locations={locations} reload={loadLocations} onMove={setMoving} />}
      {tab === 1 && <ItemsTab key={version} onMove={setMoving} />}
      {tab === 2 && <MovementsTab key={version} />}
      {moving && (
        <MoveDialog initial={moving} locations={locations.filter(l => l.isActive)} onClose={() => setMoving(null)}
          onSaved={() => { setMoving(null); setVersion(v => v + 1); }} />
      )}
    </Box>
  );
};

// ==================== Ubicaciones ====================

const emptyLocation: Partial<StorageLocation> = { code: '', name: '', sector: '', kind: 'Mixto', capacity: null, capacityUnit: '', isActive: true, notes: '' };

const LocationsTab: React.FC<{ locations: StorageLocation[]; reload: () => Promise<void>; onMove: (m: Partial<MoveForm>) => void }> = ({ locations, reload, onMove }) => {
  const [form, setForm] = useState<Partial<StorageLocation> | null>(null);
  const [detail, setDetail] = useState<StorageLocation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form) return;
    try {
      if (form.id) await api.updateStorageLocation(form.id, form);
      else await api.createStorageLocation(form);
      setForm(null);
      setError(null);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo guardar'));
    }
  };

  const remove = async (l: StorageLocation) => {
    if (!window.confirm(`¿Borrar la ubicación ${l.code}?`)) return;
    try {
      await api.deleteStorageLocation(l.id);
      reload();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo borrar'));
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Un código simple por lugar: sector, rack y estante (ej. A-01-3). Las de insumos y las de terminados no se mezclan.
        </Typography>
        <Button startIcon={<Add />} onClick={() => setForm({ ...emptyLocation })}>Nueva ubicación</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Sector</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Artículos</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Ocupación</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {locations.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Cargá las ubicaciones del depósito para empezar.</TableCell></TableRow>}
              {locations.map(l => (
                <TableRow key={l.id} hover sx={{ cursor: 'pointer', opacity: l.isActive ? 1 : 0.5 }} onClick={() => setDetail(l)}>
                  <TableCell><b>{l.code}</b>{l.name ? ` · ${l.name}` : ''}</TableCell>
                  <TableCell>{l.sector || '—'}</TableCell>
                  <TableCell><Chip size="small" label={l.kind} color={l.kind === 'Insumos' ? 'info' : l.kind === 'Terminados' ? 'success' : 'default'} /></TableCell>
                  <TableCell align="right">{l.itemsCount}</TableCell>
                  <TableCell>
                    {l.capacity ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LinearProgress variant="determinate" value={Math.min(100, (l.occupancyPct ?? 0) * 100)} color={(l.occupancyPct ?? 0) > 0.9 ? 'error' : 'primary'} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                        <Typography variant="caption">{fmtQty(l.occupied)}/{fmtQty(l.capacity)} {l.capacityUnit || ''}</Typography>
                      </Stack>
                    ) : <Typography variant="caption" color="text.secondary">{fmtQty(l.occupied)} (sin capacidad cargada)</Typography>}
                  </TableCell>
                  <TableCell align="right" onClick={e => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => setForm({ ...l })}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => remove(l)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Editar ubicación' : 'Nueva ubicación'}</DialogTitle>
        {form && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={2}>
                <TextField size="small" label="Código" value={form.code || ''} onChange={e => setForm({ ...form, code: e.target.value })} required autoFocus placeholder="A-01-3" />
                <TextField size="small" label="Sector" value={form.sector || ''} onChange={e => setForm({ ...form, sector: e.target.value })} placeholder="A" />
              </Stack>
              <TextField size="small" label="Descripción" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Rack de resinas, estante 3" />
              <TextField size="small" select label="Tipo" value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value as LocationKind })}>
                {KINDS.map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
              </TextField>
              <Stack direction="row" spacing={2}>
                <TextField size="small" type="number" label="Capacidad" value={form.capacity ?? ''} onChange={e => setForm({ ...form, capacity: e.target.value === '' ? null : num(e.target.value) })} helperText="Opcional" />
                <TextField size="small" label="Unidad" value={form.capacityUnit || ''} onChange={e => setForm({ ...form, capacityUnit: e.target.value })} placeholder="kg, bolsas" />
              </Stack>
              <FormControlLabel control={<Checkbox checked={form.isActive ?? true} onChange={e => setForm({ ...form, isActive: e.target.checked })} />} label="Activa" />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form?.code}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {detail && <LocationDetail location={detail} onClose={() => setDetail(null)} onMove={m => { setDetail(null); onMove(m); }} />}
    </>
  );
};

const LocationDetail: React.FC<{ location: StorageLocation; onClose: () => void; onMove: (m: Partial<MoveForm>) => void }> = ({ location, onClose, onMove }) => {
  const [items, setItems] = useState<StorageItem[] | null>(null);
  useEffect(() => { api.getLocationStock(location.id).then(setItems); }, [location.id]);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{location.code}{location.name ? ` · ${location.name}` : ''}</DialogTitle>
      <DialogContent>
        {!items ? <CircularProgress /> : (
          <Table size="small">
            <TableBody>
              {items.length === 0 && <TableRow><TableCell sx={{ color: 'text.secondary' }}>Vacía.</TableCell></TableRow>}
              {items.map(i => (
                <TableRow key={`${i.kind}${i.id}`}>
                  <TableCell>{i.name}</TableCell>
                  <TableCell align="right"><b>{fmtQty(i.located)}</b> {i.unit || ''}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Button size="small" onClick={() => onMove({ type: 'TRANSFER', kind: i.kind, itemId: i.id, fromLocationId: location.id })}>Trasladar</Button>
                    <Button size="small" onClick={() => onMove({ type: 'COUNT', kind: i.kind, itemId: i.id, toLocationId: location.id })}>Contar</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
    </Dialog>
  );
};

// ==================== Dónde está ====================

const ItemsTab: React.FC<{ onMove: (m: Partial<MoveForm>) => void }> = ({ onMove }) => {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'' | 'product' | 'material'>('');
  const [onlyUnlocated, setOnlyUnlocated] = useState(false);
  const [items, setItems] = useState<StorageItem[] | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.getStorageItems({ search: search || undefined, kind: kind || undefined }).then(setItems);
    }, 250);
    return () => clearTimeout(t);
  }, [search, kind]);

  const rows = (items || []).filter(i => !onlyUnlocated || Math.abs(i.unlocated) > 0.0001);

  return (
    <>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField size="small" label="Buscar" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        <ToggleButtonGroup exclusive size="small" value={kind} onChange={(_, v) => v !== null && setKind(v)}>
          <ToggleButton value="">Todo</ToggleButton>
          <ToggleButton value="material">Insumos</ToggleButton>
          <ToggleButton value="product">Terminados</ToggleButton>
        </ToggleButtonGroup>
        <FormControlLabel control={<Checkbox checked={onlyUnlocated} onChange={e => setOnlyUnlocated(e.target.checked)} />} label="Solo con stock sin ubicar" />
      </Stack>
      {!items ? <CircularProgress /> : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Artículo</TableCell>
                  <TableCell align="right">Stock total</TableCell>
                  <TableCell>Dónde está</TableCell>
                  <TableCell align="right">Sin ubicar</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>Nada para mostrar.</TableCell></TableRow>}
                {rows.map(i => (
                  <TableRow key={`${i.kind}${i.id}`} hover>
                    <TableCell>{i.name}<Typography variant="caption" color="text.secondary" display="block">{i.kind === 'material' ? 'Insumo' : 'Terminado'}</Typography></TableCell>
                    <TableCell align="right"><b>{fmtQty(i.total)}</b> {i.unit || ''}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {i.locations.map(l => <Chip key={l.locationId} size="small" variant="outlined" label={`${l.code}: ${fmtQty(l.quantity)}`} />)}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ color: i.unlocated < 0 ? 'error.main' : undefined }}>
                      {Math.abs(i.unlocated) > 0.0001 ? fmtQty(i.unlocated) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      {i.unlocated > 0 && <Button size="small" onClick={() => onMove({ type: 'IN', kind: i.kind, itemId: i.id, quantity: String(i.unlocated) })}>Ubicar</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </>
  );
};

// ==================== Movimientos ====================

const MovementsTab: React.FC = () => {
  const [from, setFrom] = useState(addDaysIso(todayIso(), -30));
  const [to, setTo] = useState(todayIso());
  const [rows, setRows] = useState<LocationMovement[] | null>(null);
  useEffect(() => { api.getLocationMovements({ from, to }).then(setRows); }, [from, to]);
  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>
      {!rows ? <CircularProgress /> : (
        <Card variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Movimiento</TableCell>
                  <TableCell>Artículo</TableCell>
                  <TableCell>Desde → hacia</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Motivo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin movimientos.</TableCell></TableRow>}
                {rows.map(m => (
                  <TableRow key={m.id}>
                    <TableCell>{new Date(m.date).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</TableCell>
                    <TableCell>{MOVE_LABEL[m.type]}{m.affectsTotal && <Chip size="small" label="mueve total" sx={{ ml: 1 }} />}</TableCell>
                    <TableCell>{m.itemName}</TableCell>
                    <TableCell>{m.type === 'COUNT' ? m.toCode : `${m.fromCode || '—'} → ${m.toCode || '—'}`}</TableCell>
                    <TableCell align="right">
                      {m.type === 'COUNT'
                        ? `${fmtQty(m.registeredQuantity)} → ${fmtQty(m.countedQuantity)} (${m.quantity >= 0 ? '+' : ''}${fmtQty(m.quantity)})`
                        : fmtQty(m.quantity)}
                    </TableCell>
                    <TableCell>{m.reason || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </>
  );
};

// ==================== Registrar movimiento ====================

type MoveForm = {
  type: LocationMovementType;
  kind: 'product' | 'material';
  itemId: number | null;
  fromLocationId: number | null;
  toLocationId: number | null;
  quantity: string;
  counted: string;
  reason: string;
  affectsTotal: boolean;
};

const MoveDialog: React.FC<{ initial: Partial<MoveForm>; locations: StorageLocation[]; onClose: () => void; onSaved: () => void }> = ({ initial, locations, onClose, onSaved }) => {
  const [f, setF] = useState<MoveForm>({
    type: 'IN', kind: 'material', itemId: null, fromLocationId: null, toLocationId: null, quantity: '', counted: '', reason: '', affectsTotal: false,
    ...initial,
  });
  const [error, setError] = useState<string | null>(null);
  const allowed = (l: StorageLocation) => l.kind === 'Mixto' || (f.kind === 'material' ? l.kind === 'Insumos' : l.kind === 'Terminados');
  const needsFrom = f.type === 'OUT' || f.type === 'TRANSFER';
  const needsTo = f.type === 'IN' || f.type === 'TRANSFER' || f.type === 'COUNT';

  const save = async () => {
    if (!f.itemId) { setError('Elegí el artículo'); return; }
    try {
      await api.createLocationMovement({
        type: f.type,
        productId: f.kind === 'product' ? f.itemId : null,
        materialId: f.kind === 'material' ? f.itemId : null,
        fromLocationId: needsFrom ? f.fromLocationId : null,
        toLocationId: needsTo ? f.toLocationId : null,
        quantity: num(f.quantity),
        countedQuantity: f.type === 'COUNT' ? num(f.counted) : null,
        reason: f.reason || null,
        affectsTotal: f.type !== 'TRANSFER' && f.affectsTotal,
      });
      if (f.affectsTotal) { loadProducts(true); loadMaterials(true); }
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'No se pudo registrar'));
    }
  };

  const select = (label: string, value: number | null, onChange: (v: number | null) => void) => (
    <TextField size="small" select label={label} value={value ?? ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} fullWidth>
      {locations.filter(allowed).map(l => <MenuItem key={l.id} value={l.id}>{l.code}{l.name ? ` · ${l.name}` : ''}</MenuItem>)}
    </TextField>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar movimiento</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <ToggleButtonGroup exclusive size="small" value={f.type} onChange={(_, v) => v && setF({ ...f, type: v })}>
            {MOVES.map(m => <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">{MOVES.find(m => m.value === f.type)?.help}</Typography>
          <ToggleButtonGroup exclusive size="small" value={f.kind} onChange={(_, v) => v && setF({ ...f, kind: v, itemId: null })}>
            <ToggleButton value="material">Insumo</ToggleButton>
            <ToggleButton value="product">Producto terminado</ToggleButton>
          </ToggleButtonGroup>
          {f.kind === 'material'
            ? <MaterialSelect value={f.itemId} onChange={id => setF({ ...f, itemId: id })} required />
            : <ProductSelect value={f.itemId} onChange={id => setF({ ...f, itemId: id })} required />}
          {needsFrom && select('Desde', f.fromLocationId, v => setF({ ...f, fromLocationId: v }))}
          {needsTo && select(f.type === 'COUNT' ? 'Ubicación contada' : 'Hacia', f.toLocationId, v => setF({ ...f, toLocationId: v }))}
          {f.type === 'COUNT'
            ? <TextField size="small" type="number" label="Cantidad contada" value={f.counted} onChange={e => setF({ ...f, counted: e.target.value })} />
            : <TextField size="small" type="number" label="Cantidad" value={f.quantity} onChange={e => setF({ ...f, quantity: e.target.value })} />}
          <TextField size="small" label={f.type === 'COUNT' ? 'Motivo de la diferencia' : 'Motivo / referencia'} value={f.reason}
            onChange={e => setF({ ...f, reason: e.target.value })} required={f.type === 'COUNT'} placeholder={f.type === 'COUNT' ? 'Ej. conteo mensual, rotura, error de carga' : 'Ej. remito 1234'} />
          {f.type !== 'TRANSFER' && (
            <FormControlLabel
              control={<Checkbox checked={f.affectsTotal} onChange={e => setF({ ...f, affectsTotal: e.target.checked })} />}
              label={f.type === 'IN' ? 'Es mercadería nueva: sumar también al stock total'
                : f.type === 'OUT' ? 'Descontar también del stock total (consumo no registrado en otro lado)'
                  : 'Ajustar también el stock total con la diferencia'}
            />
          )}
          {f.type === 'COUNT' && <Typography variant="caption" color="text.secondary">Con el ajuste, el sistema queda igual a lo que hay físicamente.</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save}>Registrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default FactoryStoragePage;
